import { startTransition, useEffect, useRef, useState } from "react";

import { intersection } from "es-toolkit";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { ActiveNodeSelector } from "@/components/doc/right-sidebar/active-node-style/ActiveNodeSelector";
import {
  ActiveNodeTitle,
  useActiveNodeTypeTitle,
} from "@/components/doc/right-sidebar/active-node-style/ActiveNodeTitle";
import { CollapsedPopover } from "@/components/doc/right-sidebar/CollapsedPopover";
import { getActiveNodes } from "@/components/expression/utils/getActiveNodes";
import { isMatrix } from "@/components/expression/utils/matrix";
import { StatelessActionsDisclosure } from "@/components/sidebar/ActionsDisclosure";
import { isModelNodeInLockedBlock } from "@/components/tiptap/lock/utils";
import { type DocumentModel } from "@/models/DocumentModel";
import { FunctionNodeModel } from "@/models/nodes/FunctionNodeModel";
import { OperatorSymbolNodeModel } from "@/models/nodes/OperatorSymbolNodeModel";
import { type NodeModel } from "@/models/nodes/types";
import { useStore } from "@/store";
import { twMerge } from "@/utils/twMerge";

type ActiveNodePanelProps = {
  node: NodeModel;
  isOpen: boolean;
  onClick: () => void;
};

const ActiveNodePanel = observer(function ActiveNodePanel({
  node,
  isOpen,
  onClick,
}: ActiveNodePanelProps) {
  const {
    view: { isRightSidebarCollapsed },
  } = useStore();
  const t = useTranslations();
  const { title, hintKey, triggerIcon } = useActiveNodeTypeTitle(node);

  if (isRightSidebarCollapsed) {
    return triggerIcon ? (
      <div className="flex flex-col gap-1 rounded-8 p-0.75 bg-gray-100 dark:bg-gray-900">
        <CollapsedPopover icon={triggerIcon} tooltipTitle={title} title={title}>
          <ActiveNodeSelector node={node} />
        </CollapsedPopover>
      </div>
    ) : null;
  }

  return (
    <StatelessActionsDisclosure
      title={
        <ActiveNodeTitle node={node} hideSymbol={isOpen}>
          {title}
        </ActiveNodeTitle>
      }
      aria-label={t("sidebar.activeNode.label")}
      isOpen={isOpen}
      onTriggerClick={onClick}
      hintKey={hintKey}
    >
      <ActiveNodeSelector node={node} />
    </StatelessActionsDisclosure>
  );
});

function getInitialOpenedNodeIds(nodes: NodeModel[]): string[] {
  const [first, second] = nodes;
  const ids: string[] = [];

  if (first) {
    ids.push(first.id);
  }

  if (second?.is(FunctionNodeModel) || isMatrix(second)) {
    ids.push(second.id);
  }

  return ids;
}

const ActiveNodePanels = observer(function ActiveNodePanels({ nodes }: { nodes: NodeModel[] }) {
  const openedNodeIds = getInitialOpenedNodeIds(nodes);
  const topNodeIdRef = useRef<string[]>(openedNodeIds);
  const [openNodeIds, setOpenNodeIds] = useState<Set<string>>(() => new Set(openedNodeIds));
  const {
    view: { isRightSidebarCollapsed },
  } = useStore();

  topNodeIdRef.current = openedNodeIds;

  useEffect(() => {
    if (topNodeIdRef.current.length > 0) {
      setOpenNodeIds(new Set(topNodeIdRef.current));
    } else {
      setOpenNodeIds(new Set());
    }
  }, [nodes.length]);

  useEffect(() => {
    setOpenNodeIds((prevState) => {
      const currentNodeIds = nodes.map((node) => node.id);
      const validOpenIds = intersection([...prevState], currentNodeIds);

      topNodeIdRef.current.forEach((nodeId) => {
        if (nodeId && !validOpenIds.includes(nodeId)) {
          validOpenIds.push(nodeId);
        }
      });

      return new Set(validOpenIds);
    });
  }, [nodes]);

  const handleClick = (node: NodeModel) => {
    setOpenNodeIds((prev) => {
      const newSet = new Set(prev);

      if (newSet.has(node.id)) {
        newSet.delete(node.id);
      } else {
        newSet.add(node.id);
      }

      return newSet;
    });
  };

  return (
    <div className={twMerge("flex flex-col w-full", isRightSidebarCollapsed && "gap-1")}>
      {nodes.map((node) => (
        <ActiveNodePanel
          key={node.id}
          node={node}
          isOpen={openNodeIds.has(node.id)}
          onClick={() => handleClick(node)}
        />
      ))}
    </div>
  );
});

export const ActiveNodeStyle = observer(function ActiveNodeStyle({ doc }: { doc: DocumentModel }) {
  const cursorContext = doc.cursorContext();
  const nodes = getActiveNodes(
    cursorContext,
    (n) => !n.is(OperatorSymbolNodeModel) && !isModelNodeInLockedBlock(n),
  );

  const [displayNodes, setDisplayNodes] = useState(nodes);
  const latestNodesRef = useRef(nodes);
  latestNodesRef.current = nodes;

  const nodeIds = nodes.map((n) => n.id).join(",");
  const cursorKey = `${cursorContext.cursor.node?.id ?? ""}:${cursorContext.cursor.index ?? ""}`;
  const cursorKeyRef = useRef(cursorKey);

  useEffect(() => {
    if (cursorKeyRef.current !== cursorKey) {
      cursorKeyRef.current = cursorKey;
      startTransition(() => setDisplayNodes(latestNodesRef.current));
    } else {
      setDisplayNodes(latestNodesRef.current);
    }
  }, [nodeIds, cursorKey]);

  if (displayNodes.length === 0) {
    return null;
  }

  return <ActiveNodePanels nodes={displayNodes} />;
});
